/**
 * `/projects/:id/reports` — the G-11 KPI dashboard (`Reports.dc.html`, ADR 0010).
 *
 * Two of the ten figures are computed by the portal; eight are entered at the monthly
 * review. The response keeps them in separate arrays, so this screen labels provenance
 * structurally rather than by remembering to — and every recorded tile shows the system
 * it came from, who entered it and which period it describes.
 *
 * A missing figure stays blank. There is no carry-forward, because a number nobody
 * recorded this month is not this month's number.
 */

import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { KPI_METRICS, kpiSpec, periodFor } from '@portal/domain';
import type { KpiMetric } from '@portal/domain';
import type { KpiDashboard, KpiTile, UpsertKpiReading } from '@portal/contracts';

import { ApiClientError, api } from '../api.js';

const LABEL: CSSProperties = {
  fontSize: 'var(--fs-label)',
  fontWeight: 600,
  letterSpacing: 'var(--tracking-label)',
  textTransform: 'uppercase',
  color: 'var(--mu)',
};

const CARD: CSSProperties = {
  background: 'var(--sf)',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--r-lg)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const NOTE: CSSProperties = { fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 };

const MONO: CSSProperties = { fontFamily: 'var(--font-mono)' };

const CHIP = (fg: string, bg: string, bd: string): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  height: 20,
  padding: '0 6px',
  borderRadius: 'var(--r-md)',
  border: `1px solid ${bd}`,
  background: bg,
  color: fg,
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: 'nowrap',
});

const OK_CHIP = CHIP('var(--ok)', 'var(--okb)', 'var(--ok-bd)');
const WARN_CHIP = CHIP('var(--wa)', 'var(--wab)', 'var(--wa-bd)');

/**
 * The colour a verdict earns. `NO_TARGET` is deliberately muted rather than amber: no
 * target agreed is not the same as a target missed.
 */
function verdictColour(verdict: KpiTile['verdict']): string {
  if (verdict === 'ON_TARGET') return 'var(--ok)';
  if (verdict === 'OFF_TARGET') return 'var(--ba)';
  return 'var(--mu)';
}

function formatValue(value: number, precision: number): string {
  return value.toFixed(precision);
}

/** Thin polyline with an emphasised endpoint. No axes: it shows direction, not values. */
function Sparkline({
  points,
  colour,
}: {
  readonly points: readonly number[];
  readonly colour: string;
}) {
  if (points.length < 2) return null;

  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min === 0 ? 1 : max - min;
  const coords = points.map((p, i) => {
    const x = (i / (points.length - 1)) * 100;
    const y = 20 - ((p - min) / span) * 16;
    return `${String(x)},${String(y)}`;
  });
  const last = coords[coords.length - 1]?.split(',') ?? ['100', '10'];

  return (
    <svg viewBox="0 0 100 22" preserveAspectRatio="none" style={{ width: '100%', height: 22 }}>
      <polyline points={coords.join(' ')} fill="none" stroke={colour} strokeWidth={1.6} />
      <circle cx={last[0]} cy={last[1]} r={2.2} fill={colour} />
    </svg>
  );
}

function ComputedTile({ tile }: { readonly tile: KpiDashboard['computed'][number] }) {
  return (
    <div
      style={{
        ...CARD,
        borderColor: 'var(--ok-bd)',
        padding: '10px 11px',
        gap: 7,
        minWidth: 0,
      }}
    >
      <span style={{ fontSize: 11.5, fontWeight: 500, lineHeight: 1.3, minHeight: 29 }}>
        {tile.label}
      </span>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 7 }}>
        <span
          style={{
            fontWeight: 600,
            fontSize: 21,
            letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums',
            color: verdictColour(tile.verdict),
          }}
        >
          {tile.value === null ? '—' : tile.value}
        </span>
        {tile.target !== null && (
          <span style={{ ...MONO, fontSize: 10.5, color: 'var(--mu)', paddingBottom: 3 }}>
            target {tile.target}
          </span>
        )}
      </div>
      <span
        style={{
          ...MONO,
          fontSize: 9.5,
          color: 'var(--mu)',
          borderTop: '1px solid var(--bd-soft)',
          paddingTop: 6,
          lineHeight: 1.35,
        }}
      >
        <span style={{ color: 'var(--ok)' }}>computed</span> · {tile.basis}
      </span>
    </div>
  );
}

function RecordedTile({
  tile,
  onEnter,
}: {
  readonly tile: KpiTile;
  readonly onEnter: (metric: KpiMetric) => void;
}) {
  const gap = tile.missingForPeriod && tile.latest === null;

  return (
    <div
      style={{
        ...CARD,
        padding: '10px 11px',
        gap: 7,
        minWidth: 0,
        ...(gap ? { borderColor: 'var(--wa-bd)', background: 'var(--wab)' } : {}),
      }}
    >
      <span
        style={{
          fontSize: 11.5,
          fontWeight: 500,
          lineHeight: 1.3,
          minHeight: 29,
          ...(gap ? { color: 'var(--wa)' } : {}),
        }}
      >
        {tile.label}
      </span>

      {tile.latest === null ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ fontWeight: 600, fontSize: 21, color: 'var(--wa)' }}>—</span>
            <span style={{ ...MONO, fontSize: 10.5, color: 'var(--wa)' }}>not recorded</span>
          </div>
          <span style={{ ...NOTE, color: 'var(--wa)', fontSize: 10.5 }}>
            Nobody has entered this figure. The tile stays blank rather than reusing an
            earlier period.
          </span>
          <button
            type="button"
            onClick={() => {
              onEnter(tile.metric);
            }}
            style={{
              height: 26,
              borderRadius: 'var(--r-md)',
              border: '1px solid var(--wa-bd)',
              background: 'var(--sf)',
              color: 'var(--tx)',
              fontSize: 11,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Enter the figure
          </button>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 7 }}>
            <span
              style={{
                fontWeight: 600,
                fontSize: 21,
                letterSpacing: '-0.02em',
                fontVariantNumeric: 'tabular-nums',
                color: verdictColour(tile.latest.verdict),
              }}
            >
              {formatValue(tile.latest.value, tile.precision)}
              {tile.unit === '%' && <span style={{ fontSize: 13 }}>%</span>}
            </span>
            {tile.latest.target !== null && (
              <span style={{ ...MONO, fontSize: 10.5, color: 'var(--mu)', paddingBottom: 3 }}>
                {tile.direction === 'HIGHER_IS_BETTER' ? '≥' : '≤'}{' '}
                {formatValue(tile.latest.target, tile.precision)}
              </span>
            )}
          </div>

          <Sparkline
            points={tile.history.map((h) => h.value)}
            colour={verdictColour(tile.latest.verdict)}
          />
        </>
      )}

      {/* Provenance is not optional decoration — it is why the number is believable. */}
      <span
        style={{
          ...MONO,
          fontSize: 9.5,
          color: 'var(--mu)',
          borderTop: `1px solid ${gap ? 'var(--wa-bd)' : 'var(--bd-soft)'}`,
          paddingTop: 6,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <span>
          {tile.cadence.toLowerCase()} · {tile.expectedSource.replace('_', '-').toLowerCase()}
        </span>
        {tile.latest === null ? (
          <span>never recorded</span>
        ) : (
          <span style={tile.asOfEarlierPeriod !== null ? { color: 'var(--wa)' } : undefined}>
            {tile.latest.recordedBy.initials} · {tile.latest.recordedAt.slice(0, 10)}
            {tile.asOfEarlierPeriod !== null && ` · as of ${tile.asOfEarlierPeriod}`}
          </span>
        )}
      </span>
    </div>
  );
}

export function ReportsRoute() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [period, setPeriod] = useState(() => periodFor('AVAILABILITY', new Date()));
  const [entering, setEntering] = useState<KpiMetric | null>(null);
  const [value, setValue] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [note, setNote] = useState('');

  const dashboard = useQuery({
    queryKey: ['kpi', id, period],
    queryFn: () => api.kpiDashboard(id, period),
  });

  const record = useMutation({
    mutationFn: (input: UpsertKpiReading) => api.recordKpi(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['kpi', id] });
      setEntering(null);
      setValue('');
      setSourceUrl('');
      setNote('');
    },
  });

  const spec = entering === null ? null : kpiSpec(entering);

  /** The periods the selector offers: this month back six, plus the last two quarters. */
  const periods = useMemo(() => {
    const now = new Date();
    const out: string[] = [];
    for (let back = 0; back < 6; back += 1) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - back, 1));
      out.push(periodFor('AVAILABILITY', d));
    }
    for (let back = 0; back < 2; back += 1) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - back * 3, 1));
      const q = periodFor('MTTP_CRITICAL_CVE', d);
      if (!out.includes(q)) out.push(q);
    }
    return out;
  }, []);

  if (dashboard.isPending) {
    return (
      <div style={{ padding: 24, ...NOTE }}>
        Loading the KPI review…
      </div>
    );
  }

  if (dashboard.isError) {
    const message =
      dashboard.error instanceof ApiClientError
        ? dashboard.error.message
        : 'The KPI dashboard could not be loaded.';
    return (
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <strong style={{ color: 'var(--ba)' }}>{message}</strong>
        <Link to={`/projects/${id}`} style={{ fontSize: 12.5 }}>
          Back to the project
        </Link>
      </div>
    );
  }

  const data = dashboard.data;

  return (
    <div style={{ padding: '18px 24px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em' }}>
            Reports &amp; KPIs
          </h1>
          <span style={NOTE}>
            {data.projectName} · the G-11 set, with every figure showing where it came from and
            as of when.
          </span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {data.missingCount > 0 && (
            <span style={WARN_CHIP}>
              {data.missingCount} not recorded for {data.period}
            </span>
          )}
          <select
            value={period}
            onChange={(event) => {
              setPeriod(event.target.value);
            }}
            style={{
              height: 30,
              borderRadius: 'var(--r-md)',
              border: '1px solid var(--bd)',
              background: 'var(--sf)',
              color: 'var(--tx)',
              fontSize: 12,
              padding: '0 8px',
            }}
          >
            {periods.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* the honest framing — ADR 0010 */}
      <div
        style={{
          ...CARD,
          borderLeft: '2.5px solid var(--wv)',
          padding: '10px 13px',
          flexDirection: 'row',
          gap: 10,
        }}
      >
        <span style={{ ...NOTE, flex: 1 }}>
          <b style={{ color: 'var(--tx)' }}>
            Two of these figures are computed here. Eight are entered by hand at the monthly
            review.
          </b>{' '}
          The portal reads only GitLab; availability, SLA, vulnerability and coverage figures
          live in systems it links to but does not query. Rather than invent them, the review
          records them with a source, a person and a date. A figure with no provenance would be
          worse than no figure.
        </span>
        <span style={{ ...MONO, fontSize: 10, color: 'var(--wv)', whiteSpace: 'nowrap' }}>
          ADR 0010
        </span>
      </div>

      {/* computed */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ ...LABEL, color: 'var(--ok)' }}>Computed by the portal</span>
        <span style={OK_CHIP}>{data.computed.length}</span>
        <span style={{ ...NOTE, marginLeft: 'auto' }}>
          Recalculated on every read — nobody enters these
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 9 }}>
        {data.computed.map((tile) => (
          <ComputedTile key={tile.metric} tile={tile} />
        ))}
        {data.scoreTrend.length > 1 && (
          <div style={{ ...CARD, gridColumn: 'span 2', padding: '10px 11px', gap: 6 }}>
            <span style={{ fontSize: 11.5, fontWeight: 500 }}>
              Mandatory score, last {data.scoreTrend.length} closed audits
            </span>
            <Sparkline
              points={data.scoreTrend.map((p) => p.percent)}
              colour="var(--ac)"
            />
            <span style={{ ...MONO, fontSize: 9.5, color: 'var(--mu)' }}>
              {data.scoreTrend.map((p) => `${String(Math.round(p.percent))}%`).join(' → ')}
            </span>
          </div>
        )}
      </div>

      {/* recorded */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 2 }}>
        <span style={{ ...LABEL, color: 'var(--wv)' }}>Recorded at the monthly review</span>
        <span style={CHIP('var(--wv)', 'var(--wvb)', 'var(--wv-bd)')}>{KPI_METRICS.length}</span>
        <span style={{ ...NOTE, marginLeft: 'auto' }}>
          Each carries its source system, who entered it and the period it describes
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 9 }}>
        {data.recorded.map((tile) => (
          <RecordedTile
            key={tile.metric}
            tile={tile}
            onEnter={(metric) => {
              setEntering(metric);
              setValue('');
            }}
          />
        ))}
      </div>

      {/* entry form — only when a figure is being recorded */}
      {entering !== null && spec !== null && (
        <div
          style={{
            ...CARD,
            borderColor: 'var(--wv-bd)',
            padding: '12px 13px',
            gap: 10,
            maxWidth: 520,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--wv)' }}>
              Record {spec.label}
            </span>
            <span style={{ ...MONO, fontSize: 10, color: 'var(--mu)', marginLeft: 'auto' }}>
              {periodFor(entering, new Date())} · {spec.cadence.toLowerCase()}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
              <span style={LABEL}>Value ({spec.unit})</span>
              <input
                value={value}
                onChange={(event) => {
                  setValue(event.target.value);
                }}
                inputMode="decimal"
                style={{
                  height: 30,
                  borderRadius: 'var(--r-md)',
                  border: '1px solid var(--bd)',
                  padding: '0 8px',
                  fontSize: 12,
                  fontFamily: 'var(--font-mono)',
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 2 }}>
              <span style={LABEL}>Source link — optional</span>
              <input
                value={sourceUrl}
                onChange={(event) => {
                  setSourceUrl(event.target.value);
                }}
                placeholder={`https://… (${spec.expectedSource.toLowerCase()})`}
                style={{
                  height: 30,
                  borderRadius: 'var(--r-md)',
                  border: '1px solid var(--bd)',
                  padding: '0 8px',
                  fontSize: 12,
                }}
              />
            </label>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={LABEL}>Note — optional</span>
            <input
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
              style={{
                height: 30,
                borderRadius: 'var(--r-md)',
                border: '1px solid var(--bd)',
                padding: '0 8px',
                fontSize: 12,
              }}
            />
          </label>

          <span style={NOTE}>
            The system is recorded as <b style={{ color: 'var(--tx)' }}>{spec.expectedSource}</b>,
            and your name and today's date are stored with it. The target is taken from this
            project's default at the moment you save, so changing the target later will not
            re-judge this reading.
          </span>

          {record.isError && (
            <span style={{ ...NOTE, color: 'var(--ba)' }}>
              {record.error instanceof ApiClientError
                ? record.error.message
                : 'The figure could not be saved.'}
            </span>
          )}

          <div style={{ display: 'flex', gap: 7 }}>
            <button
              type="button"
              disabled={value.trim() === '' || Number.isNaN(Number(value)) || record.isPending}
              onClick={() => {
                record.mutate({
                  metric: entering,
                  period: periodFor(entering, new Date()),
                  value: Number(value),
                  unit: spec.unit,
                  sourceSystem: spec.expectedSource as UpsertKpiReading['sourceSystem'],
                  sourceUrl: sourceUrl.trim() === '' ? null : sourceUrl.trim(),
                  note: note.trim() === '' ? null : note.trim(),
                });
              }}
              style={{
                height: 30,
                padding: '0 14px',
                borderRadius: 'var(--r-md)',
                border: '1px solid var(--ac)',
                background: 'var(--ac)',
                color: '#fff',
                fontSize: 12.5,
                fontWeight: 500,
                cursor: 'pointer',
                opacity: value.trim() === '' ? 0.45 : 1,
              }}
            >
              {record.isPending ? 'Saving…' : 'Save reading'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEntering(null);
              }}
              style={{
                height: 30,
                padding: '0 12px',
                borderRadius: 'var(--r-md)',
                border: '1px solid var(--bd)',
                background: 'var(--sf)',
                fontSize: 12.5,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* the consequence of option A, stated where it applies */}
      <div
        style={{
          ...CARD,
          borderLeft: '2.5px solid var(--wa)',
          padding: '10px 13px',
          flexDirection: 'row',
          gap: 10,
        }}
      >
        <span style={{ ...NOTE, flex: 1 }}>
          <b style={{ color: 'var(--tx)' }}>
            Recording these by hand leaves G-13 at Partial, permanently.
          </b>{' '}
          G-13 asks that KPI collection be automated “so reporting does not depend on someone
          remembering to gather figures manually” — which is what this screen depends on. It is{' '}
          <span style={MONO}>[R]</span>, not <span style={MONO}>[M]</span>, so the mandatory
          score is untouched; the item should read Partial with ADR 0010 as its justification
          rather than Verified.
        </span>
      </div>
    </div>
  );
}
